// Everything the leaderboard does to its rows: group, search, sort, paginate. Pure.
import type { LeaderboardRow } from "@/lib/dashboard/rows";

export const ALL_TAB = "all";

export interface StatusTab {
  id: string;
  count: number;
}

// The statuses that mean something to a reader, in the order they should appear. Anything
// else the backend sends follows, alphabetically, rather than being dropped.
const STATUS_ORDER = ["scored", "not qualified", "failed review", "in queue"];

/** One tab per status actually present, so an empty group never shows up as a dead tab. */
export function statusTabs(rows: LeaderboardRow[]): StatusTab[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const key = r.status ?? "unknown";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const rank = (s: string) => {
    const i = STATUS_ORDER.indexOf(s);
    return i === -1 ? STATUS_ORDER.length : i;
  };
  const tabs = [...counts.entries()]
    .sort(([a], [b]) => (rank(a) === rank(b) ? a.localeCompare(b) : rank(a) - rank(b)))
    .map(([id, count]) => ({ id, count }));
  return [{ id: ALL_TAB, count: rows.length }, ...tabs];
}

export function filterRows(rows: LeaderboardRow[], tab: string, query: string): LeaderboardRow[] {
  const needle = query.trim().toLowerCase();
  return rows.filter((r) => {
    if (tab !== ALL_TAB && (r.status ?? "unknown") !== tab) return false;
    return needle === "" || r.hotkey.toLowerCase().includes(needle);
  });
}

export type SortDirection = "asc" | "desc";

/** `overall`, `stage1`, `stage2`, `submit`, `status`, or `cat:<category>`. */
export type SortKey = string;

export const CATEGORY_SORT_PREFIX = "cat:";

function valueOf(row: LeaderboardRow, key: SortKey): number | string | null {
  if (key.startsWith(CATEGORY_SORT_PREFIX)) return row.categories[key.slice(CATEGORY_SORT_PREFIX.length)] ?? null;
  switch (key) {
    case "overall":
      return row.overall;
    case "stage1":
      return row.stage1.score;
    case "stage2":
      return row.stage2.score;
    case "submit":
      return row.lastSubmit;
    case "status":
      return row.status;
    default:
      return row.place;
  }
}

/**
 * Missing values sort last in both directions. A null is "not measured", not "worst":
 * flipping the direction should not float unknowns to the top of the table.
 */
export function sortRows(rows: LeaderboardRow[], key: SortKey, direction: SortDirection): LeaderboardRow[] {
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = valueOf(a, key);
    const vb = valueOf(b, key);
    if (va === null && vb === null) return a.place - b.place;
    if (va === null) return 1;
    if (vb === null) return -1;
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * sign;
    return String(va).localeCompare(String(vb)) * sign;
  });
}

export interface Page<T> {
  items: T[];
  page: number;
  pages: number;
  total: number;
}

export function paginate<T>(items: T[], page: number, perPage: number): Page<T> {
  const pages = Math.max(1, Math.ceil(items.length / perPage));
  const current = Math.min(Math.max(1, page), pages);
  const start = (current - 1) * perPage;
  return { items: items.slice(start, start + perPage), page: current, pages, total: items.length };
}

/** The 1-based page `hotkey` lands on in `rows`, or null when it is not there. */
export function pageOfHotkey(rows: LeaderboardRow[], hotkey: string, perPage: number): number | null {
  const index = rows.findIndex((r) => r.hotkey === hotkey);
  return index === -1 ? null : Math.floor(index / perPage) + 1;
}

export interface MinerPosition {
  row: LeaderboardRow;
  /**
   * Place among the scored miners, by score - the rank the backend gives and the miner
   * page prints, so the two never disagree. Not the row's position in the table: that
   * puts a failed review with a high raw score above every scored miner. Null for a miner
   * that is not scored (during upload, nobody is).
   */
  place: number | null;
  total: number;
}

/** Where a followed miner stands in this competition, or null when it did not enter it. */
export function locateMiner(rows: LeaderboardRow[], hotkey: string | null): MinerPosition | null {
  if (!hotkey) return null;
  const row = rows.find((r) => r.hotkey === hotkey);
  if (!row) return null;
  const score = row.overall;
  if (row.status !== "scored" || score === null) return { row, place: null, total: rows.length };
  const ahead = rows.filter((r) => r.status === "scored" && r.overall !== null && r.overall > score).length;
  return { row, place: ahead + 1, total: rows.length };
}
